import { useCallback } from "react";
import { useParams } from "react-router";

import InputSubmit from "./input-submit";
import { IQuestion, PageModule, RegFieldsEnum, SqsSocketTypes } from "../../../../../types/working-model";
import { CreateLiveChatOrQuestionMessage } from "../../../../../connection/save-chat-message";
import { useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { toggleRegistrationModal } from "../../../../../store/actions/event/event-actions";
import { useDispatch } from "react-redux";
import { getStorageItem } from "../../../../../utils/local-storage";
import { useTranslation } from "react-i18next";
import { setModuleQuestionsComments } from "../../../../../store/actions/event/questions-actions";
import { QUESTIONS_PAGE_LIMIT } from "./questions";

interface IAddQuestionCommentProps {
	module: PageModule;
	question: IQuestion;
	sessionUuid: string;
	onFocus: () => void;
	onBlur: () => void;
	focusCommentInput: boolean;
	setFocusCommentInput: React.Dispatch<React.SetStateAction<boolean>>;
}

const AddQuestionComment: React.FC<IAddQuestionCommentProps> = ({
	module,
	question,
	sessionUuid,
	onFocus,
	onBlur,
	focusCommentInput,
	setFocusCommentInput,
}) => {

	const params = useParams<any>();
	const language = params?.language || 'en';

	const dispatch = useDispatch();
	const { t } = useTranslation("session");

	const event = useTypedSelector(state => state.LiveEventReducer.eventBundle?.event);
	const sessions = useTypedSelector(state => state.LiveEventReducer.eventBundle?.sessions);
	const channel = useTypedSelector(state => state.LiveEventReducer.eventBundle?.channel);
	const blProfileUser = useTypedSelector(state => state.LiveEventReducer.blProfileUser);
	const comments = useTypedSelector(state => state.QuestionsReducer.comments);
	const questions = useTypedSelector(state => state.QuestionsReducer.questions);

	const getSession = useCallback(() => {
		return sessions?.find(session => session.uuid === sessionUuid);
	}, [sessions, sessionUuid]);

	const session = getSession();
	if (!session || !channel) return null;
	return (
		<div className="add-question-comment-container">
			<InputSubmit
				maxLength={150}
				onFocus={() => {
					const token = getStorageItem('Bl-Profile-Token');
					if (!token) {
						// show profile details modal and return
						dispatch(toggleRegistrationModal(sessionUuid));
					} else {
						onFocus();
					}
				}}
				focus={focusCommentInput}
				setFocus={setFocusCommentInput}
				onBlur={onBlur}
				onSubmitClick={async value => {
					if (!value.trim().length) return Promise.resolve({});
					// submit to microservices api
					const token = getStorageItem('Bl-Profile-Token');
					if (!token) {
						// show profile details modal and return
						dispatch(toggleRegistrationModal(sessionUuid));
						return Promise.resolve({ doNotClear: true });
					}
					// need to update this body and also the microservice to handle different message types
					const body = {
						message_type: SqsSocketTypes.QUESTION_COMMENT,
						comment: value,
						first_name: blProfileUser?.profile?.[channel][RegFieldsEnum.first_name],
						last_name: blProfileUser?.profile?.[channel][RegFieldsEnum.last_name],
						profile: blProfileUser?.profile,
						event,
						channel,
						session: session.session,
						email: blProfileUser?.email,
						module_id: module.id,
						question_id: question.id,
						session_uuid: session.uuid,
						language,
					};
					const result = await CreateLiveChatOrQuestionMessage(session.session, body, token, SqsSocketTypes.QUESTION_COMMENT);
					if (result?.spam) {
						// Add the users message directly to their list.
						// It will not be sent to anyone else because the backend will not trigger the socket
						let commentsCopy = new Map(comments[result.module_id][result.question_id]);
						// update the matched comment list
						let arrayedComments = Array.from(commentsCopy);
						// do not list more than QUESTIONS_PAGE_LIMIT messages to save the user browser memory
						if (arrayedComments.length > QUESTIONS_PAGE_LIMIT) {
							arrayedComments = arrayedComments.slice(0, QUESTIONS_PAGE_LIMIT);
						}
						// update map by putting new message at beginning
						commentsCopy = new Map([[result.id, result], ...arrayedComments]);
						dispatch(setModuleQuestionsComments(result.module_id, result.question_id, commentsCopy));

						// increment question comments length in place
						const updatedQns = new Map(questions[result.module_id]);
						const qn = updatedQns.get(result.question_id);
						if (qn) {
							updatedQns.set(qn.id, {
								...qn,
								total_comments: qn?.total_comments ? Number(qn?.total_comments) + 1 : 1,
							});
						}
					}
					return Promise.resolve({});
				}}
				module={module}
				placeholder={t("Write a comment")}
				buttonText="Post"
				containerClass="no-padding"
			/>
		</div>
	);
};

export default AddQuestionComment;
